import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Spinner,
  Tabs,
  Tab,
  Table,
  Badge,
} from "react-bootstrap";
import moment from "moment";
import { FETCH_PROPERTY_SUCCESS, FETCH_VEHICLE_SUCCESS } from "../../../app/actions/actions";
import { ROUTES } from "../../../config/routingUrl";

export default function RapportsCaisseTab() {
  const  dispatch =  useDispatch();
  const navigate = useNavigate();
  const { caisses, loadingCaisse } = useSelector((state) => state.caisses);
  const [key, setKey] = useState("liste"); // active tab

  // ✅ Regroupement logique
  const resumes = useMemo(() => {
    const map = {};

    caisses.forEach((op) => {
      const id = op.vehicle?._id ?? op.property?._id ?? "AUTRE";
      const label = op.vehicle
        ? `${op.vehicle.brand} ${op.vehicle.model}`
        : op.property
          ? op.property.name
          : "Non affecté";

      if (!map[id]) {
        map[id] = {
          label,
          nb: 0,
          total: 0,
          last: null,
          rows: [],
          byType: {},
          byCurrency: {},
          byMethod: {},
          byTypeAndCurrency: {},
        };
      }

      const group = map[id];
      group.nb += 1;
      group.total += op.amount;
      group.rows.push(op);
      if (!group.last || new Date(op.createdAt) > new Date(group.last)) {
        group.last = op.createdAt;
      }

      // Totaux simples
      group.byType[op.type] = (group.byType[op.type] || 0) + op.amount;
      group.byCurrency[op.currency] = (group.byCurrency[op.currency] || 0) + op.amount;
      group.byMethod[op.method] = (group.byMethod[op.method] || 0) + op.amount;

      // Totaux par type + devise
      if (!group.byTypeAndCurrency[op.type]) group.byTypeAndCurrency[op.type] = {};
      group.byTypeAndCurrency[op.type][op.currency] =
        (group.byTypeAndCurrency[op.type][op.currency] || 0) + op.amount;
    });

    return Object.values(map);
  }, [caisses]);

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <h4>Caisses — Rapports & Enregistrements</h4>
      </Card.Header>

      <Card.Body>
        {loadingCaisse ? (
          <div className="d-flex justify-content-center my-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
            {/* Onglet 1 : Liste brute */}
            <Tab eventKey="liste" title="Liste des enregistrements">
              <div style={{ maxHeight: 500, overflowY: "auto" }}>
                <Table striped bordered hover size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Réservation </th>
                      <th>Patrimoine</th>
                      <th>Type</th>
                      <th>Montant</th>
                      <th>Devise</th>
                      <th>Mode</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caisses.map((op) => (
                      <tr key={op._id}>
                        <td>{moment(op.createdAt).format("DD/MM/YYYY")}</td>
                        <td>
                          <span onClick={() => {
                            if (op && op.vehicle) {
                              dispatch({ type: FETCH_VEHICLE_SUCCESS, payload: op.vehicle });
                              dispatch({ type: FETCH_PROPERTY_SUCCESS, payload: {} });
                              navigate(`/${ROUTES.DASHBOARD}/${ROUTES.RESERVATION_VIEW}/${op._id}`)
                            } else {
                              dispatch({ type: FETCH_VEHICLE_SUCCESS, payload: {} });
                              dispatch({ type: FETCH_PROPERTY_SUCCESS, payload: op.property });
                              navigate(`/${ROUTES.DASHBOARD}/${ROUTES.RESERVATION_VIEW}/${op._id}`)
                            }
                          }}
                            style={{ cursor: "pointer" }}
                            className="text-decoration-underline">{op?.reservation?.ordre}</span>
                        </td>
                        <td>{op.vehicle?.brand || op.property?.name || "-"}</td>
                        <td>{op.type}</td>
                        <td>{op.amount.toLocaleString()}</td>
                        <td>{op.currency}</td>
                        <td>{op.method}</td>
                        <td>{op.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>

            {/* Onglet 2 : Rapport par groupe */}
            <Tab eventKey="rapport" title="Rapport par véhicule / propriété">
              <div style={{ maxHeight: 500, overflowY: "auto" }}>
                <Table striped bordered hover size="sm" responsive>
                  <tbody>
                    {resumes.map((r, index) => (
                      <tr key={index}>
                        <td colSpan={7}>
                          <div className="fw-bold mb-2 text-primary">
                            {r.label} — {r.nb} opération(s), Total : {r.total.toLocaleString()} – Dernière : {moment(r.last).format("DD/MM/YYYY HH:mm")}
                          </div>

                          {/* Résumé par type + devise */}
                          <Table size="sm" bordered className="mb-0">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th colSpan={6}>Totaux par devise</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(r.byTypeAndCurrency).map(([type, currencyMap]) => (
                                <tr key={type}>
                                  <td>
                                    <Badge bg="secondary" className="me-2">{type}</Badge>
                                  </td>
                                  <td colSpan={6}>
                                    {Object.entries(currencyMap).map(([currency, total]) => (
                                      <span key={currency} className="me-4">
                                        <strong>{currency}</strong>: {total.toLocaleString()}
                                      </span>
                                    ))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>
        )}
      </Card.Body>
    </Card>
  );
}
